import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * One ready-to-copy caption. Lives beside the snapshots on the package page —
 * captions are something an agent copies while writing the post, so they sit
 * where the rest of the listing package is rather than behind a click.
 */

const SocialPostBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
`;

const SocialPostLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0e7e7a;
  margin-bottom: 6px;
`;

const SocialPostText = styled.div`
  font-size: 0.92rem;
  line-height: 1.5;
  color: #1a1a1a;
  white-space: pre-line;
  margin-bottom: 10px;
`;

const CopyButton = styled.button`
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 14px;
  min-height: 44px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const InlineError = styled.p`
  margin: 8px 0 0;
  color: #b91c1c;
  font-size: 0.82rem;
`;

/**
 * @param {{ post: { label: string, text: string }, onCopied: () => void }} props
 */
const SocialPost = ({ post, onCopied }) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const copiedTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    []
  );
  const copy = async () => {
    setCopyError(false);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(post.text);
      setCopied(true);
      onCopied();
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyError(true);
    }
  };
  return (
    <SocialPostBox>
      <SocialPostLabel>{post.label}</SocialPostLabel>
      <SocialPostText>{post.text}</SocialPostText>
      <CopyButton type="button" onClick={copy}>
        {copied ? "Copied!" : "Copy"}
      </CopyButton>
      {copyError && (
        <InlineError role="alert">
          Couldn't copy automatically. Select the text above to copy it.
        </InlineError>
      )}
    </SocialPostBox>
  );
};

export default SocialPost;
